"use client";

import { formatPrice } from "@/lib/format";
import { luxuryImagesByBoutique } from "@/lib/luxury-images";
import { useCart } from "@/lib/cart-context";

interface ChefPick {
  badge: string;
  rating: number;
  detail: string;
  name: string;
  price: number;
  inStock: boolean;
  image: string;
}

// Curated images pulled from the luxury image pools (one per concept).
const POOL = {
  epicerieFine: luxuryImagesByBoutique["epicerie-fine"] ?? [],
  epicerieMonde: luxuryImagesByBoutique["epicerie-monde"] ?? [],
  comptoirCafes: luxuryImagesByBoutique["comptoir-cafes"] ?? [],
  cave: luxuryImagesByBoutique["cave-exception"] ?? [],
};

const PICKS: ChefPick[] = [
  { badge: "Coup de cœur", rating: 4.9, detail: "Miel · Bissap · Café Touba", name: "Coffret Découverte Terroir", price: 32000, inStock: true, image: POOL.epicerieFine[0] },
  { badge: "Nouveauté", rating: 4.8, detail: "Hibiscus & Baobab Bio", name: "Bissap Royal 1L", price: 4500, inStock: true, image: POOL.epicerieMonde[1] },
  { badge: "Édition limitée", rating: 5.0, detail: "Ataya · Menthe · Sucre candi", name: "Coffret Thé & Dentelle", price: 28500, inStock: true, image: POOL.comptoirCafes[2] },
  { badge: "Best-seller", rating: 4.9, detail: "Basmati · Safran · Cardamome", name: "Riz & Épices d'Or", price: 21000, inStock: true, image: POOL.epicerieMonde[3] },
  { badge: "Prestige", rating: 5.0, detail: "Bordeaux · Champagne · Truffe", name: "Coffret Cave d'Exception", price: 168000, inStock: true, image: POOL.cave[12] },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-gold"
      >
        <path d="M12 2 15.09 8.26 22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2Z"></path>
      </svg>
      <span className="font-display text-cream text-sm">{rating.toFixed(1)}</span>
    </div>
  );
}

function ChefPickCard({ product, index }: { product: ChefPick; index: number }) {
  const { addItem } = useCart();
  return (
    <>
      <StarRating rating={product.rating} />
      <p className="mt-2 text-cream/45 text-[10px] tracking-[0.1em] uppercase">
        {product.detail}
      </p>
      <h4 className="mt-1 font-serif text-cream text-[14px] leading-snug min-h-[2.6rem]">
        {product.name}
      </h4>
      <div className="mt-3 pt-3 border-t border-cream/10 flex items-center justify-between gap-2">
        <div>
          <div className="font-display text-gold text-lg leading-none">
            {formatPrice(product.price)}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase text-cream/55">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-400"></span>
            En stock
          </div>
        </div>
        <button
          aria-label={`Ajouter ${product.name} au panier`}
          onClick={() =>
            addItem({
              id: `chef-pick-${index}-${product.name}`,
              name: product.name,
              price: product.price,
              image: product.image,
              boutiqueName: "Le Choix du Chef",
            })
          }
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink hover:border-gold transition-all"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14"></path>
            <path d="M12 5v14"></path>
          </svg>
        </button>
      </div>
    </>
  );
}

export function ChefPicksSection() {
  return (
    <section id="chef-picks" className="relative bg-ink text-cream py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 ink-gradient"></div>
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(200,162,74,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(200,162,74,0.6) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      ></div>

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="ornament-line mb-6 justify-center">
            <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
              Le Choix du Chef
            </span>
          </div>
          <h2 className="font-display text-cream text-4xl md:text-6xl leading-[1.05]">
            Sélections
            <br />
            <span className="gold-text italic">du moment.</span>
          </h2>
          <p className="mt-6 text-cream/65 font-serif text-lg leading-relaxed">
            Une curatèle renouvelée chaque semaine par notre chef de rayon. Le
            meilleur du terroir français, sublimé.
          </p>
        </div>

        {/* Picks grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {PICKS.map((p, idx) => (
            <article
              key={idx}
              className="group relative bg-cream/5 backdrop-blur-sm border border-cream/15 rounded-xl overflow-hidden hover:border-gold/50 transition-all duration-500 hover:-translate-y-0.5 flex flex-col"
            >
              {/* Visual */}
              <div
                className="relative aspect-square overflow-hidden"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(200,162,74,0.18) 0%, rgba(26,23,20,0.6) 100%)",
                }}
              >
                {p.image && (
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="eager"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                {/* Badge */}
                <div className="absolute top-3 left-3 z-10">
                  <span className="px-2.5 py-1 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold bg-gold text-ink">
                    {p.badge}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 flex flex-col p-4">
                <ChefPickCard product={p} index={idx} />
              </div>
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 flex justify-center">
          <a
            href="#la-table"
            className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-cream/30 text-cream hover:border-gold hover:text-gold transition-colors tracking-[0.2em] uppercase text-sm"
          >
            Voir toute la sélection du chef
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14"></path>
              <path d="m19 12-7 7-7-7"></path>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
